import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Share2, Copy, CheckCircle2, Gift, LogIn, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const REWARD_TEXT = { redeemer: "$1.00", referrer: "$0.50" };

export default function ReferralWidget() {
  const { user, login, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) { setData(null); return; }
    setLoading(true);
    fetch(`${API}/api/bonus/my-referral-code`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const code = data?.coupon?.code;
  const rewardCents = data?.reward_cents ?? 50;
  const redeemerCents = data?.coupon?.amount_cents ?? 100;
  const uses = data?.coupon?.uses_count ?? 0;

  const shareText = code
    ? `Ganhei $${(redeemerCents / 100).toFixed(2)} de BONUS no @ufocoin usando meu código ${code}. Resgata o teu antes que acabe 👽💸`
    : "";
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";

  const copy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* denied */ }
  };

  const shareOnX = () => {
    if (!code) return;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="referral"
      data-testid="referral-widget"
      className="relative py-16 sm:py-24 border-t border-fuchsia-400/20 bg-gradient-to-b from-black via-fuchsia-950/10 to-black"
    >
      <div className="mx-auto max-w-6xl px-4 grid gap-8 md:grid-cols-2 md:items-center">
        {/* Left: pitch */}
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-fuchsia-300">
            // REFERRAL PROGRAM
          </p>
          <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl text-white tracking-tighter leading-none">
            GANHE <span className="text-fuchsia-400">$0.50</span>
            <br />POR AMIGO
          </h2>
          <p className="mt-4 text-sm text-white/70 max-w-md">
            Cada piloto tem um código único de indicação. Compartilha com quem
            quiseres: eles ganham <span className="text-emerald-300">{REWARD_TEXT.redeemer}</span> no
            BONUS wallet e tu ganhas <span className="text-emerald-300">{REWARD_TEXT.referrer}</span> a
            cada resgate. Sem limite de indicações.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-[10px] font-mono tracking-widest text-white/50">
            <span className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5 rounded-sm">
              <Gift className="h-3 w-3 text-fuchsia-300" /> BONUS INSTANTÂNEO
            </span>
            <span className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5 rounded-sm">
              <Users className="h-3 w-3 text-cyan-300" /> ILIMITADO
            </span>
          </div>
        </div>

        {/* Right: card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-sm border border-fuchsia-400/40 bg-black/70 p-6 space-y-4 backdrop-blur-sm relative overflow-hidden"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-fuchsia-500/20 blur-3xl"
          />

          {authLoading || loading ? (
            <div data-testid="referral-loading" className="animate-pulse space-y-3">
              <div className="h-3 w-32 bg-white/10 rounded-sm" />
              <div className="h-8 w-full bg-white/10 rounded-sm" />
              <div className="h-9 w-full bg-white/10 rounded-sm" />
            </div>
          ) : !user ? (
            <div data-testid="referral-cta-login" className="space-y-4">
              <p className="font-mono text-[10px] tracking-[0.3em] text-fuchsia-300">
                LOGIN PARA GERAR
              </p>
              <p className="text-white/70 text-sm">
                Faz login com Google pra receber o teu código de indicação vitalício.
              </p>
              <Button
                onClick={login}
                data-testid="referral-login-btn"
                className="w-full bg-fuchsia-500 text-black hover:bg-fuchsia-400 font-mono tracking-widest text-xs rounded-sm"
              >
                <LogIn className="h-4 w-4 mr-2" /> ENTRAR COM GOOGLE
              </Button>
            </div>
          ) : code ? (
            <div className="space-y-4 relative">
              <p className="font-mono text-[10px] tracking-[0.3em] text-fuchsia-300">
                SEU CÓDIGO
              </p>
              <div className="flex items-center gap-2">
                <code
                  data-testid="referral-code"
                  className="flex-1 font-mono text-base sm:text-lg text-fuchsia-200 break-all bg-black/60 border border-fuchsia-400/30 rounded-sm px-3 py-2"
                >
                  {code}
                </code>
                <button
                  onClick={copy}
                  data-testid="referral-copy-btn"
                  aria-label="Copiar código"
                  className="p-2 border border-fuchsia-400/30 rounded-sm text-fuchsia-200 hover:bg-fuchsia-500/10"
                >
                  {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>

              <Button
                onClick={shareOnX}
                data-testid="referral-share-x-btn"
                className="w-full bg-fuchsia-500 text-black hover:bg-fuchsia-400 font-mono tracking-widest text-xs rounded-sm"
              >
                <Share2 className="h-4 w-4 mr-2" /> COMPARTILHAR NO X
              </Button>

              <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-3 text-center">
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-white/40">RESGATES</p>
                  <p data-testid="referral-uses" className="font-display font-black text-2xl text-white tracking-tighter">
                    {uses}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-widest text-white/40">GANHOS</p>
                  <p data-testid="referral-earnings" className="font-display font-black text-2xl text-emerald-300 tracking-tighter">
                    ${((uses * rewardCents) / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="referral-error" className="text-white/60 text-sm">
              Não conseguimos carregar o teu código. Tenta atualizar a página.
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
